import React, { useState } from 'react';
import Modal from '../../components/ui/Modal';
import Btn from '../../components/ui/Btn';
import { Input, Select } from '../../components/ui/Input';
import { condicoesPagamentoApi } from '../../api/condicoesPagamento';
import { COLORS } from '../../theme';

const FORMAS = [
  { value: 'boleto', label: 'Boleto' },
  { value: 'pix', label: 'Pix' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cheque', label: 'Cheque' }
];

const OPCOES_PARCELAS_CARTAO = Array.from({ length: 24 }, (_, i) => ({ value: String(i + 1), label: i === 0 ? 'À vista (1x)' : `${i + 1}x` }));

function sugerirNome(forma, prazosBoleto, diaUnico, parcelasCartao) {
  const rotuloForma = FORMAS.find(f => f.value === forma)?.label || '';
  if (forma === 'boleto') {
    const dias = prazosBoleto.filter(Boolean);
    if (dias.length === 0) return '';
    return dias.length === 1 && dias[0] === '0' ? 'Boleto à vista' : `Boleto ${dias.join('/')}`;
  }
  if (forma === 'cartao') return `Cartão ${parcelasCartao}x`;
  if (diaUnico === '0' || !diaUnico) return `${rotuloForma} à vista`;
  return `${rotuloForma} ${diaUnico} dias`;
}

export default function CriarCondicaoPagamentoModal({ formaInicial, onFechar, onCriada }) {
  const [forma, setForma] = useState(formaInicial || 'boleto');
  const [prazosBoleto, setPrazosBoleto] = useState(['30']);
  const [diaUnico, setDiaUnico] = useState('0');
  const [parcelasCartao, setParcelasCartao] = useState('1');
  const [nome, setNome] = useState('');
  const [nomeEditadoManualmente, setNomeEditadoManualmente] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const nomeSugerido = sugerirNome(forma, prazosBoleto, diaUnico, parcelasCartao);
  const nomeExibido = nomeEditadoManualmente ? nome : nomeSugerido;

  const alterarPrazoBoleto = (idx, valor) => setPrazosBoleto(p => p.map((v, i) => i === idx ? valor : v));
  const adicionarPrazoBoleto = () => setPrazosBoleto(p => [...p, '']);
  const removerPrazoBoleto = (idx) => setPrazosBoleto(p => p.filter((_, i) => i !== idx));

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');

    let prazos_dias, parcelas;
    if (forma === 'boleto') {
      const dias = prazosBoleto.map(d => d.trim()).filter(Boolean);
      if (dias.length === 0 || !dias.every(d => /^\d+$/.test(d))) {
        setErro('Preencha todos os prazos do boleto com números de dias válidos.');
        return;
      }
      prazos_dias = dias.join(',');
      parcelas = dias.length;
    } else if (forma === 'cartao') {
      prazos_dias = '0';
      parcelas = Number(parcelasCartao);
    } else {
      if (!/^\d+$/.test(diaUnico)) { setErro('Informe o prazo em dias (use 0 para à vista).'); return; }
      prazos_dias = diaUnico;
      parcelas = 1;
    }

    const nomeFinal = nomeExibido.trim();
    if (!nomeFinal) { setErro('Dê um nome para essa condição de pagamento.'); return; }

    setSalvando(true);
    try {
      const resultado = await condicoesPagamentoApi.criar({ nome: nomeFinal, forma_pagamento: forma, prazos_dias, parcelas });
      onCriada(resultado.id);
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      titulo="Nova Condição de Pagamento"
      onFechar={onFechar}
      largura={500}
      rodape={
        <>
          <Btn variante="ghost" onClick={onFechar}>Cancelar</Btn>
          <Btn onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
        </>
      }
    >
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Select label="Forma de Pagamento" options={FORMAS} value={forma} onChange={e => setForma(e.target.value)} />

        {forma === 'boleto' && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>
              Prazos (em dias, um por parcela do boleto)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {prazosBoleto.map((prazo, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input type="number" min="0" value={prazo} onChange={e => alterarPrazoBoleto(idx, e.target.value)}
                    placeholder="Ex: 30" style={{ width: 120 }} />
                  <span style={{ fontSize: 12, color: COLORS.textLight }}>dias</span>
                  {prazosBoleto.length > 1 && (
                    <Btn tamanho="sm" variante="danger" type="button" onClick={() => removerPrazoBoleto(idx)}>Remover</Btn>
                  )}
                </div>
              ))}
            </div>
            <Btn tamanho="sm" variante="secondary" type="button" onClick={adicionarPrazoBoleto} style={{ marginTop: 8 }}>
              + Adicionar Prazo
            </Btn>
          </div>
        )}

        {forma === 'cartao' && (
          <Select label="Número de Parcelas" options={OPCOES_PARCELAS_CARTAO} value={parcelasCartao} onChange={e => setParcelasCartao(e.target.value)} />
        )}

        {!['boleto', 'cartao'].includes(forma) && (
          <Input label="Prazo (em dias, 0 = à vista)" type="number" min="0" value={diaUnico} onChange={e => setDiaUnico(e.target.value)} />
        )}

        <Input
          label="Nome (aparece na lista de condições)"
          value={nomeExibido}
          onChange={e => { setNome(e.target.value); setNomeEditadoManualmente(true); }}
          placeholder={nomeSugerido || 'Ex: Boleto 30/60/90'}
        />

        {erro && <p style={{ color: COLORS.danger, fontSize: 13, margin: 0 }}>{erro}</p>}
      </form>
    </Modal>
  );
}
